import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface WeekNavigationProps {
  currentWeekStart: Date
  currentWeekEnd: Date
  onNavigateToWeek: (direction: 'previous' | 'next') => void
  onNavigateToToday: () => void
  className?: string
}

export function WeekNavigation({
  currentWeekStart,
  currentWeekEnd,
  onNavigateToWeek,
  onNavigateToToday,
  className
}: WeekNavigationProps) {
  const formatWeekRange = (start: Date, end: Date): string => {
    // Ensure we have proper Date objects
    const startDate = new Date(start)
    const endDate = new Date(end)
    
    const startStr = startDate.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short'
    })
    const endStr = endDate.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
    
    // If same month, show: "1 - 7 dec 2024"
    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
      return `${startDate.getDate()} - ${endStr}`
    }
    
    // If different months: "29 nov - 5 dec 2024"
    return `${startStr} - ${endStr}`
  }

  const isCurrentWeek = (): boolean => {
    const today = new Date()
    const weekStart = new Date(currentWeekStart)
    const weekEnd = new Date(currentWeekEnd)
    return today >= weekStart && today <= weekEnd
  }

  return (
    <div className={cn(
      'flex flex-col sm:flex-row sm:items-center justify-between bg-white rounded-lg border border-gray-200 p-4 gap-4',
      className
    )}>
      <div className="flex items-center justify-center sm:justify-start space-x-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigateToWeek('previous')}
          className="h-8 w-8 p-0"
          aria-label="Vorige week"
          title="Vorige week"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <div className="text-center min-w-[180px] sm:min-w-[200px]">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            {formatWeekRange(currentWeekStart, currentWeekEnd)}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500">
            ISO week {getISOWeekNumber(new Date(currentWeekStart))}
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigateToWeek('next')}
          className="h-8 w-8 p-0"
          aria-label="Volgende week"
          title="Volgende week"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex items-center justify-center sm:justify-end space-x-2">
        {!isCurrentWeek() && (
          <Button
            variant="outline"
            size="sm"
            onClick={onNavigateToToday}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
            aria-label="Ga naar huidige week"
          >
            <Calendar className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Vandaag</span>
            <span className="sm:hidden">Nu</span>
          </Button>
        )}
        
        <div className="text-xs text-gray-500">
          {isCurrentWeek() && (
            <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
              Deze week
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function getISOWeekNumber(date: Date): number {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNumber = utcDate.getUTCDay() || 7
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNumber)
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1))
  return Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
}
